import { supabase } from './supabase';
import { getCurrentHospitalId } from './hospitalContext';

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface RazorpayOrder {
  id: string;
  amount: number;
  currency: string;
  receipt?: string;
  status: string;
}

export interface RazorpayPaymentResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

/**
 * Load Razorpay checkout script
 */
export function loadRazorpayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay script'));
    document.body.appendChild(script);
  });
}

/**
 * Create a Razorpay order via Supabase Edge Function
 * @param planId - Subscription plan ID
 * @param baseAmount - Base amount (tax-exclusive)
 * @param totalAmount - Total amount (base + tax) to charge
 * @param currency - Currency code (default: INR)
 */
export async function createRazorpayOrder(
  planId: string,
  baseAmount: number,
  totalAmount: number,
  currency: string = 'INR'
): Promise<{ orderId: string; key: string } | null> {
  try {
    const currentHospitalId = await getCurrentHospitalId();
    
    if (!currentHospitalId) {
      throw new Error('No hospital context');
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase.functions.invoke('create-razorpay-order', {
      body: {
        planId,
        baseAmount,
        totalAmount,
        currency,
        hospitalId: currentHospitalId
      }
    });

    if (error) {
      console.error('Error creating Razorpay order - Full error object:', error);
      console.error('Error context:', error.context);
      console.error('Error message:', error.message);
      
      // Try to extract error message from response
      let errorMessage = 'Failed to create payment order';
      
      // Check if error has a response body we can parse
      if (error.context?.body) {
        try {
          const errorBody = typeof error.context.body === 'string' 
            ? JSON.parse(error.context.body) 
            : error.context.body;
          console.error('Parsed error body:', errorBody);
          if (errorBody.error) {
            errorMessage = errorBody.error;
            if (errorBody.details) {
              errorMessage += `: ${JSON.stringify(errorBody.details)}`;
            }
          }
        } catch (e) {
          console.error('Error parsing error body:', e);
        }
      }
      
      // Also check error.message
      if (error.message && error.message !== 'Edge Function returned a non-2xx status code') {
        errorMessage = error.message;
      }
      
      const enhancedError = new Error(errorMessage);
      (enhancedError as any).originalError = error;
      throw enhancedError;
    }

    return data;
  } catch (error: any) {
    console.error('Error in createRazorpayOrder:', error);
    
    // If it's already our enhanced error, just throw it
    if (error.message && error.originalError) {
      throw error;
    }
    
    // Otherwise, create a user-friendly error
    const errorMessage = error.message || 'Failed to create payment order. Please try again.';
    throw new Error(errorMessage);
  }
}

/**
 * Verify Razorpay payment signature and activate subscription
 */
export async function verifyRazorpayPayment(
  paymentResponse: RazorpayPaymentResponse
): Promise<boolean> {
  try {
    const currentHospitalId = await getCurrentHospitalId();
    
    if (!currentHospitalId) {
      throw new Error('No hospital context');
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase.functions.invoke('verify-razorpay-payment', {
      body: {
        ...paymentResponse,
        hospitalId: currentHospitalId
      }
    });

    if (error) {
      console.error('Error verifying Razorpay payment:', error);
      throw error;
    }

    return data?.success === true;
  } catch (error) {
    console.error('Error in verifyRazorpayPayment:', error);
    throw error;
  }
}

/**
 * Open Razorpay checkout
 */
export async function openRazorpayCheckout(
  orderId: string,
  key: string,
  amount: number,
  currency: string,
  planName: string,
  hospitalName: string,
  onSuccess: (response: RazorpayPaymentResponse) => void,
  onError: (error: any) => void
): Promise<void> {
  try {
    await loadRazorpayScript();

    if (!window.Razorpay) {
      throw new Error('Razorpay script not loaded');
    }

    const options = {
      key, // Razorpay key from backend
      amount: amount * 100, // Convert to paise
      currency,
      name: 'AM CARE',
      description: `Subscription: ${planName}`,
      order_id: orderId,
      // Only prefill name - let user enter email and contact
      prefill: {
        name: hospitalName || 'Hospital Admin'
        // Don't include email and contact - let user enter them
      },
      theme: {
        color: '#3b82f6'
      },
      handler: onSuccess,
      modal: {
        ondismiss: () => {
          onError(new Error('Payment cancelled by user'));
        },
        // Ensure modal is on top
        escape: true,
        backdropclose: false
      },
      notes: {
        plan_name: planName,
        hospital_id: (await getCurrentHospitalId()) || ''
      },
      // Enable all payment methods
      method: {
        netbanking: true,
        card: true,
        upi: true,
        wallet: true,
        emi: false
      },
      // Make sure all fields are editable
      readonly: {
        contact: false,
        email: false,
        name: false
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', (response: any) => {
      onError(new Error(response.error.description || 'Payment failed'));
    });
    razorpay.open();
  } catch (error) {
    console.error('Error opening Razorpay checkout:', error);
    onError(error);
  }
}

